import React, { useEffect, useState } from 'react';
import { connect } from '../../../redux/store';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { I18n } from '@kineticdata/react';
import {
  StaticTypeahead,
  C,
  addToast,
  addToastAlert,
} from '@kineticdata/bundle-common';
import { List } from 'immutable';

const TeamSuggestionContent = ({ suggestion }) => {
  return (
    <>
      {suggestion.get('parentName') && (
        <div className="small">{suggestion.get('parentName')}</div>
      )}
      <div className="large">{suggestion.get('localName')}</div>
    </>
  );
};

const mapTeamToTeamOptions = team => {
  const [localName, ...parents] = team.name.split('::').reverse();
  return {
    localName,
    name: team.name,
    parentName: parents.reverse().join('::'),
  };
};

const enhanceWithTeams = connect(state => ({
  technicalOwnerTeams: state.app.subteamsMap
    .get(C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME, List())
    .map(mapTeamToTeamOptions),
  fetchSubteams: state.app.actions.fetchSubteams,
}));

export const NewPortfolioModal = enhanceWithTeams(
  ({
    toggle,
    createPortfolio,
    onSuccess,
    fetchSubteams,
    technicalOwnerTeams,
  }) => {
    // Fetch the teams lists
    useEffect(
      () => {
        fetchSubteams({ teamName: C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME });
      },
      [fetchSubteams],
    );

    const [name, setName] = useState('');
    const [type, setType] = useState('Internal');
    const [technicalOwners, setTechnicalOwners] = useState(List());
    const [pending, setPending] = useState(false);

    return (
      <Modal isOpen={true} toggle={toggle}>
        <div className="modal-header">
          <h4 className="modal-title">
            <button type="button" className="btn btn-link" onClick={toggle}>
              <I18n>Close</I18n>
            </button>
            <span>
              <I18n>New Portfolio</I18n>
            </span>
          </h4>
        </div>
        <ModalBody>
          <div className="form p-3">
            <div className="form-group required">
              <label htmlFor="portfolio-name" className="field-label">
                <I18n>New Portfolio Name</I18n>
              </label>
              <input
                type="text"
                id="portfolio-name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="checkbox required">
              <label htmlFor="portfolio-type-radio" className="field-label">
                <I18n>Type</I18n>
              </label>
              <label htmlFor="portfolio-type-public">
                <input
                  name="portfolio-type-radio"
                  type="radio"
                  id="portfolio-type-public"
                  checked={type === 'Public'}
                  onChange={() => setType('Public')}
                />
                <I18n>Public</I18n>
              </label>
              <label htmlFor="portfolio-type-internal">
                <input
                  name="portfolio-type-radio"
                  type="radio"
                  id="portfolio-type-internal"
                  checked={type === 'Internal'}
                  onChange={() => setType('Internal')}
                />
                <I18n>Internal</I18n>
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="portfolio-tech-owners" className="field-label">
                <I18n>Technical Owners</I18n>
              </label>
              <StaticTypeahead
                options={technicalOwnerTeams.toJS()}
                value={technicalOwners.toJS()}
                onChange={setTechnicalOwners}
                search={{
                  fields: [
                    { name: 'localName', operator: 'matches' },
                    { name: 'parentName', operator: 'matches' },
                  ],
                }}
                valueProp="name"
                components={{ SuggestionContent: TeamSuggestionContent }}
                multiple={true}
                minSearchLength={0}
                id="portfolio-tech-owners"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-success"
            type="button"
            disabled={!name || !type || pending}
            onClick={() => {
              setPending(true);
              createPortfolio({
                values: {
                  Name: name,
                  Type: type,
                  'Technical Owners': technicalOwners.toJS().map(t => t.name),
                },
                success: submission => {
                  if (typeof onSuccess === 'function') {
                    onSuccess(submission);
                  }
                  addToast('Portfolio created successfully');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to create portfolio',
                    message: error.message,
                  });
                },
                complete: toggle,
              });
            }}
          >
            {!pending ? (
              <I18n>Create Portfolio</I18n>
            ) : (
              <>
                <span className="fa fa-fw fa-spinner fa-spin" />
                <I18n>Creating</I18n>
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>
    );
  },
);

export const NewServiceModal = enhanceWithTeams(
  ({
    toggle,
    createService,
    onSuccess,
    portfoliosList,
    portfolioId,
    fetchSubteams,
    technicalOwnerTeams,
  }) => {
    // Fetch the teams lists
    useEffect(
      () => {
        fetchSubteams({ teamName: C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME });
      },
      [fetchSubteams],
    );

    const [name, setName] = useState('');
    const [type, setType] = useState('Internal');
    const [technicalOwners, setTechnicalOwners] = useState(List());
    // If portfolioId is provided, use that as the initial value for portfolios
    const [portfolios, setPortfolios] = useState(
      portfolioId
        ? portfoliosList
            .filter(p => p.values[C.FIELD_PORTFOLIO_ID] === portfolioId)
            .map(p => ({
              id: p.values[C.FIELD_PORTFOLIO_ID],
              name: p.values['Name'],
            }))
        : List([]),
    );
    const [pending, setPending] = useState(false);

    return (
      <Modal isOpen={true} toggle={toggle}>
        <div className="modal-header">
          <h4 className="modal-title">
            <button type="button" className="btn btn-link" onClick={toggle}>
              <I18n>Close</I18n>
            </button>
            <span>
              <I18n>New Service</I18n>
            </span>
          </h4>
        </div>
        <ModalBody>
          <div className="form p-3">
            <div className="form-group required">
              <label htmlFor="service-name" className="field-label">
                <I18n>New Service Name</I18n>
              </label>
              <input
                type="text"
                id="servicen-name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="checkbox required">
              <label htmlFor="service-type-radio" className="field-label">
                <I18n>Type</I18n>
              </label>
              <label htmlFor="service-type-public">
                <input
                  name="service-type-radio"
                  type="radio"
                  id="service-type-public"
                  checked={type === 'Public'}
                  onChange={() => setType('Public')}
                />
                <I18n>Public</I18n>
              </label>
              <label htmlFor="service-type-internal">
                <input
                  name="service-type-radio"
                  type="radio"
                  id="service-type-internal"
                  checked={type === 'Internal'}
                  onChange={() => setType('Internal')}
                />
                <I18n>Internal</I18n>
              </label>
            </div>
            <div className="form-group">
              <label htmlFor="service-tech-owners" className="field-label">
                <I18n>Technical Owners</I18n>
              </label>
              <StaticTypeahead
                options={technicalOwnerTeams.toJS()}
                value={technicalOwners.toJS()}
                onChange={setTechnicalOwners}
                search={{
                  fields: [
                    { name: 'localName', operator: 'matches' },
                    { name: 'parentName', operator: 'matches' },
                  ],
                }}
                valueProp="name"
                components={{ SuggestionContent: TeamSuggestionContent }}
                multiple={true}
                minSearchLength={0}
                id="service-tech-owners"
              />
            </div>
            <div className="form-group">
              <label htmlFor="service-portfolios" className="field-label">
                <I18n>Portfolio(s)</I18n>
                <small className="d-block">
                  <I18n>What portfolio(s) does this service belong to?</I18n>
                </small>
              </label>
              <StaticTypeahead
                valueProp="id"
                labelProp="name"
                options={portfoliosList.toJS().map(p => ({
                  id: p.values[C.FIELD_PORTFOLIO_ID],
                  name: p.values['Name'],
                }))}
                search={{ fields: [{ name: 'name', operator: 'matches' }] }}
                value={portfolios.toJS()}
                onChange={setPortfolios}
                multiple={true}
                minSearchLength={0}
                id="service-portfolios"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-success"
            type="button"
            disabled={!name || !type || pending}
            onClick={() => {
              setPending(true);
              createService({
                values: {
                  Name: name,
                  Type: type,
                  'Technical Owners': technicalOwners.toJS().map(t => t.name),
                  [C.FIELD_PORTFOLIO_ID]: portfolios
                    .sortBy(p => p.name)
                    .toJS()
                    .map(p => p.id),
                },
                success: submission => {
                  if (typeof onSuccess === 'function') {
                    onSuccess(submission);
                  }
                  addToast('Service created successfully');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to create service',
                    message: error.message,
                  });
                },
                complete: toggle,
              });
            }}
          >
            {!pending ? (
              <I18n>Create Service</I18n>
            ) : (
              <>
                <span className="fa fa-fw fa-spinner fa-spin" />
                <I18n>Creating</I18n>
              </>
            )}
          </button>
        </ModalFooter>
      </Modal>
    );
  },
);

export const NewAppendixModal = ({ toggle, createAppendix, onSuccess }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('Internal');
  const [pending, setPending] = useState(false);

  return (
    <Modal isOpen={true} toggle={toggle}>
      <div className="modal-header">
        <h4 className="modal-title">
          <button type="button" className="btn btn-link" onClick={toggle}>
            <I18n>Close</I18n>
          </button>
          <span>
            <I18n>New Appendix</I18n>
          </span>
        </h4>
      </div>
      <ModalBody>
        <div className="form p-3">
          <div className="form-group required">
            <label htmlFor="appendix-name" className="field-label">
              <I18n>New Appendix Name</I18n>
            </label>
            <input
              type="text"
              id="appendix-name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="checkbox required">
            <label htmlFor="appendix-type-radio" className="field-label">
              <I18n>Type</I18n>
            </label>
            <label htmlFor="appendix-type-public">
              <input
                name="appendix-type-radio"
                type="radio"
                id="appendix-type-public"
                checked={type === 'Public'}
                onChange={() => setType('Public')}
              />
              <I18n>Public</I18n>
            </label>
            <label htmlFor="appendix-type-internal">
              <input
                name="appendix-type-radio"
                type="radio"
                id="appendix-type-internal"
                checked={type === 'Internal'}
                onChange={() => setType('Internal')}
              />
              <I18n>Internal</I18n>
            </label>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-success"
          type="button"
          disabled={!name || !type || pending}
          onClick={() => {
            setPending(true);
            createAppendix({
              values: {
                Name: name,
                Type: type,
              },
              success: submission => {
                if (typeof onSuccess === 'function') {
                  onSuccess(submission);
                }
                addToast('Appendix created successfully');
              },
              failure: error => {
                addToastAlert({
                  severity: 'danger',
                  title: 'Failed to create appendix',
                  message: error.message,
                });
              },
              complete: toggle,
            });
          }}
        >
          {!pending ? (
            <I18n>Create Appendix</I18n>
          ) : (
            <>
              <span className="fa fa-fw fa-spinner fa-spin" />
              <I18n>Creating</I18n>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
};
